import React, { useEffect, useMemo, useState } from "react";
import UploadButton from "../components/ArtPage/UploadButton";
import ArtImageBox from "../components/ArtPage/ArtConfig";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  getFirestore,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  Timestamp,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import SendButton from "../components/ArtPage/SendButton";
import ErrorFileType from "../components/ArtPage/ErrorFileType";
import OrderSent from "../components/ArtPage/OrderSent";

import "./ArtServices.scss";

export default function ArtServices() {
  const db = getFirestore();
  const storage = getStorage();

  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [showError, setShowError] = useState(false);
  const [instructions, setInstructions] = useState([]);
  const [names, setNames] = useState([]);
  const [success, setSuccess] = useState(false);
  const [authUser, setAuthUser] = useState(undefined);
  const navigate = useNavigate();

  const allowedTypes = useMemo(
    () => [
      // PNG
      "image/png",
      // JPEG/JPG
      "image/jpeg",
      "image/jpg",
      // SVG
      "image/svg",
      "image/svg+xml",
      // BMP
      "image/bmp",
      "image/x-ms-bmp",
      // TIFF
      "image/tiff",
      "image/x-tiff",
    ],
    []
  );

  useEffect(() => {
    const filePreviews = files.map((file) => URL.createObjectURL(file));
    setPreviews(filePreviews);
    return () => {
      filePreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [files]);

  useEffect(() => {
    const auth = getAuth();
    return onAuthStateChanged(auth, (user) => setAuthUser(user ?? null));
  }, []);

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;

    const filtered = selected.filter((file) =>
      allowedTypes.includes(file.type)
    );

    setFiles((prev) => [...prev, ...filtered]);
    setInstructions((prev) => [...prev, ...Array(filtered.length).fill("")]);
    setNames((prev) => [
      ...prev,
      ...filtered.map((file) => file.name.replace(/\.[^/.]+$/, "")),
    ]);
    if (filtered.length < selected.length) {
      setShowError(true);
    }

    e.target.value = "";
  };

  const handleInstructionChange = (index, value) => {
    const updated = [...instructions];
    updated[index] = value;
    setInstructions(updated);
  };

  const handleNameChange = (index, value) => {
    const updated = [...names];
    updated[index] = value;
    setNames(updated);
  };

  const generateUniqueId = async () => {
    let exists = true;
    let id = "";
    while (exists) {
      id = Math.floor(10000000 + Math.random() * 90000000).toString();
      const docRef = doc(db, "artOrders", id);
      const snapshot = await getDoc(docRef);
      exists = snapshot.exists();
    }
    return id;
  };

  const handleSubmit = async () => {
    try {
      if (authUser === undefined) {
        alert("Finishing sign-in setup. Please try again in a moment.");
        return;
      }

      if (!authUser) {
        alert("Please log in to submit an art order.");
        navigate("/login");
        return;
      }

      const customerId = authUser.uid;
      const orderId = await generateUniqueId();

      // First, create the Firestore doc so Storage rules can validate uploads.
      await setDoc(doc(db, "artOrders", orderId), {
        attachments: [],
        createdAt: Timestamp.now(),
        status: "Pending",
        customerId,
      });

      // Wait for Firestore doc to be readable/propagated.
      let canAccess = false;
      for (let i = 0; i < 10; i++) {
        const snap = await getDoc(doc(db, "artOrders", orderId));
        if (snap.exists()) {
          canAccess = true;
          break;
        }
        await new Promise((res) => setTimeout(res, 300));
      }
      if (!canAccess) {
        alert("Upload failed: order record not accessible yet. Please retry.");
        return;
      }

      const orderData = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const baseName = names[i].replace(/\.[^/.]+$/, "");
        const cleanName = `${baseName}`;
        const fileRef = ref(storage, `artServices/${orderId}/${cleanName}`);

        await uploadBytes(fileRef, file);
        const url = await getDownloadURL(fileRef);

        orderData.push({
          name: cleanName,
          url,
          instructions: instructions[i],
        });
      }

      await updateDoc(doc(db, "artOrders", orderId), {
        attachments: orderData,
      });

      setSuccess(true);
      setFiles([]);
      setPreviews([]);
      setInstructions([]);
      setNames([]);
    } catch (error) {
      console.error("Art order submit failed:", error);
      alert(
        "Couldn't submit your art order. If you're logged in, please try again or contact support."
      );
    }
  };

  const canSubmit =
    files.length > 0 &&
    names.every((name) => name.trim() !== "") &&
    instructions.every((instr) => instr.trim() !== "");

  return (
    <>
      {showError && <ErrorFileType onClick={() => setShowError(false)} />}

      <div className="artServices">
        <div className="artServices__wrap">
	          <div className="artServices__left">
	            <header className="artServices__header">
	              <div className="artServices__headerTop">
	                <h1 className="artServices__title">Need Help with Art?</h1>
	                {previews.length > 0 && (
	                  <div
	                    className="artServices__headerPreviews"
	                    aria-label="Uploaded files"
	                  >
	                    <div className="artServices__headerPreviewsScroller">
	                      {previews.map((src, index) => (
	                        <div className="artServices__previewCard" key={index}>
	                          <ArtImageBox
	                            index={index}
	                            src={src}
	                            nameValue={names[index]}
	                            onNameChange={handleNameChange}
	                            instructionsValue={instructions[index]}
	                            onInstructionsChange={handleInstructionChange}
	                          />
	                        </div>
	                      ))}
	                    </div>
	                  </div>
	                )}
	              </div>
	              <p className="artServices__subtitle">
	                Upload your file(s) below and our team will assist in making
	                your work ready for print.
	              </p>
	              <div className="artServices__actions">
                  <UploadButton onClick={handleFileChange} />
                  {files.length > 0 && (
                    <>
                      <SendButton onClick={handleSubmit} disabled={!canSubmit} />
                      {authUser === null && (
                        <button
                          type="button"
                          className="artServices__linkButton"
                          onClick={() => navigate("/login")}
                        >
                          Log in required
                        </button>
                      )}
                    </>
                  )}
                </div>
            </header>

            <section
              className="artServices__stepsSection"
              aria-label="How it works"
            >
              <p className="artServices__stepsIntro">
                Follow these simple steps to get print ready art files:
              </p>

              <div className="artServices__stepsGrid">
                <ol className="artServices__steps" aria-label="Steps">
                  <li className="artServices__step">
                    <img
                      className="artServices__stepIcon"
                      src="/sodtf-01.jpg"
                      alt=""
                      aria-hidden="true"
                    />
                    <p className="artServices__stepText">
                      Upload your file. Be sure to include clear instructions
                      for all necessary edits.
                    </p>
                  </li>
                  <li className="artServices__step">
                    <img
                      className="artServices__stepIcon"
                      src="/sodtf-02.jpg"
                      alt=""
                      aria-hidden="true"
                    />
                    <p className="artServices__stepText">
                      We’ll respond with an exact quote based on your requested
                      edit(s).
                    </p>
                  </li>
                  <li className="artServices__step">
                    <img
                      className="artServices__stepIcon"
                      src="/sodtf-03.jpg"
                      alt=""
                      aria-hidden="true"
                    />
                    <p className="artServices__stepText">
                      Once the quote is paid and processed, we’ll send back your
                      print-ready art file.
                    </p>
                  </li>
                  <li className="artServices__step">
                    <img
                      className="artServices__stepIcon"
                      src="/sodtf-04.jpg"
                      alt=""
                      aria-hidden="true"
                    />
                    <p className="artServices__stepText">
                      Upload your new file to our{" "}
                      <a
                        href="/dtf-sheet-builder"
                        style={{
                          borderBottom: "2px solid #94C940",
                          color: "inherit",
                          textDecoration: "none",
                          display: "inline-block",
                          verticalAlign: "top",
                        }}
                      >
                        Design Portal
                      </a>{" "}
                      to make transfers at any size and quantity.
                    </p>
                  </li>
                </ol>

	                <div
	                  className="artServices__uploads artServices__uploads--empty"
	                  aria-label="Uploads"
	                >
	                  <div
	                    className="artServices__uploadsEmpty"
	                    aria-label="Upload placeholder"
	                  >
	                    <img
	                      className="artServices__uploadsPlaceholder"
	                      src="/doggo.png"
	                      alt="Example upload placeholder"
	                    />
	                    <img
	                      className="artServices__uploadsPlaceholder"
	                      src="/doggo%20edit.png"
	                      alt="Example upload placeholder with edits"
	                    />
	                  </div>
	                </div>
	              </div>
	            </section>

          </div>

          <aside className="artServices__pricing" aria-label="Quote pricing">
            <h2 className="artServices__pricingTitle">
              Art Service Quote Pricing
            </h2>

            <div className="artServices__priceBlock">
              <div className="artServices__priceLine">
                <span className="artServices__priceTier">Easy</span>{" "}
                <span className="artServices__priceAmount">$15</span>
              </div>
              <p className="artServices__priceDesc">
                Simple tasks such as solid background removal or minor
                touch-ups.
              </p>
            </div>

            <div className="artServices__priceBlock">
              <div className="artServices__priceLine">
                <span className="artServices__priceTier">Average</span>{" "}
                <span className="artServices__priceAmount">$30</span>
              </div>
              <p className="artServices__priceDesc">
                More detailed edits like complex background removal, moderate
                vectorization, or color corrections.
              </p>
            </div>

            <div className="artServices__priceBlock">
              <div className="artServices__priceLine">
                <span className="artServices__priceTier">Hard</span>{" "}
                <span className="artServices__priceAmount">$50</span>
              </div>
              <p className="artServices__priceDesc">
                Complex vector tracing, redrawing, or combining multiple
                elements into one design.
              </p>
            </div>

            <div className="artServices__priceBlock">
              <div className="artServices__priceLine">
                <span className="artServices__priceTier">Conceptual Art</span>{" "}
                <span className="artServices__priceAmount">Custom Quote</span>
              </div>
              <p className="artServices__priceDesc">
                Original artwork or full design ideas created from scratch based
                on your vision.
              </p>
            </div>
          </aside>
        </div>

        {success && <OrderSent onClick={() => navigate("/")} />}
      </div>
    </>
  );
}
